import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import InventoryLogos from '../../screens/PhoneDetailsScreen/InventoryBadges/InventoryBadges';
import Loader from '../../UI/Loader/Loader';
import { magicStrings, getStoreStatus, getNextOpeningTime } from '../utils';
import StoreStatus from '../StoreStatus/StoreStatus';
import { isKiosk } from '../../../utils/isKiosk';
import useTmsDeptHours from '../../../hooks/useTmsDeptHours';
import {
  BladeButtonWrapper,
  Store,
  StoreIdentifier,
  DistanceWrapper,
  Icon,
  StoreContact,
  Location,
  Time,
  SelectBtn,
  Chevron,
  ContactUsLink,
  VirtualMobilistArrow,
} from './styles';

const labels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

interface Props {
  className?: string,
  index: number,
  store: Record<string, any>,
  isMapList: boolean,
  allStoreLogos: Record<string, any>[],
  activeStore?: Record<string, any>,
  onClick?: () => void,
  isReserveList?: boolean,
  inventoryLabels?: Record<string, any>,
  page?: string,
  apiError?: boolean,
  inventoryStatus?: string,
  selectedSKU?: string,
  selectedCarrier?: string,
  isKioskWelcome?: boolean,
}

// eslint-disable-next-line complexity
const StoreItem = ({
  className = '',
  store,
  activeStore,
  isMapList,
  isReserveList = false,
  allStoreLogos,
  index,
  onClick = () => null,
  inventoryLabels,
  page,
  apiError = false,
  inventoryStatus = '',
  selectedSKU = '',
  selectedCarrier = '',
  isKioskWelcome = false,
}: Props) => {
  const { locale } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isTempClosed, setIsTempClosed] = useState(false);
  const [openingTime, setOpeningTime] = useState(undefined);
  const [closingTime, setClosingTime] = useState(undefined);
  // Do second call to get the TMS department store hours. 
  // Using store hours only as new data set is incomplete for rest of store information.
  const tmsDept = useTmsDeptHours({
    store,
    isKioskWelcome,
  });

  // Keep selected store based on initial store data set
  const isActive = activeStore && store?.commonInfo?.lclId === activeStore?.commonInfo?.lclId;
  const storeLogo = allStoreLogos.find(
    ({ storeName }) => storeName.toLowerCase() === store.storeBanner?.name.toLowerCase(),
  );

  // Use TMS despartment store hours
  useEffect(() => {
    if (tmsDept && tmsDept.commonInfo?.businessHours.length > 0) {
      const {
        commonInfo: { businessHours: allStoreHours },
      } = tmsDept;
      const firstStoreHours = allStoreHours[0];
      const status = getStoreStatus(
        firstStoreHours.openingTime,
        firstStoreHours.closingTime,
      );
      setIsOpen(status && !firstStoreHours.closed);
      setIsTempClosed(false);
      setOpeningTime(
        getNextOpeningTime(
          allStoreHours,
          firstStoreHours?.openingTime,
          locale,
        ),
      );
      setClosingTime(firstStoreHours.closingTime);
    } else {
      setIsTempClosed(true);
      setOpeningTime(undefined);
      setClosingTime(undefined);
    }
  }, [locale, tmsDept]);

  // Exit and show loader if bullseye data isn't ready yet
  if (!store) {
    return <Loader />;
  }
  const isKioskMode = isKiosk();
  const addressLine1 = `${store.commonInfo.address.line1},`;
  const addressLine2 = `${store.commonInfo.address.city}, ${store.commonInfo.address.province} `;
  // if undefined got passed as the store, don't create the store markup
  const storeItem = (
    <Store className={className}>
      <StoreIdentifier>
        <DistanceWrapper char={isMapList ? labels[index] : ''}>
          <p>
            <strong>
              {store.commonInfo.distance}
              km
            </strong>
          </p>
        </DistanceWrapper>
        <Icon>
          <img
            src={storeLogo?.logo?.asset?.localImage?.publicURL}
            alt={storeLogo?.logo?.altText}
          />
        </Icon>
      </StoreIdentifier>
      <StoreContact>
        <Location data-testid="store-location" isKioskWelcome={isKioskWelcome}>
          <p>
            <strong>
              <span>
                {addressLine1}
              </span>
              <br />
              <span>
                {addressLine2}
                {tmsDept?.commonInfo.address.postalCode}
              </span>
            </strong>
          </p>

          {!isKioskWelcome && (
            <Time className="not-active">
              <StoreStatus
                isOpen={isOpen}
                isTempClosed={isTempClosed}
                closingTime={closingTime}
                openingTime={openingTime}
              />
            </Time>
          )}
          <br />
          <InventoryLogos
            activeStore={activeStore}
            inventoryLabels={inventoryLabels}
            page={page}
            apiError={apiError}
            inventoryStatus={inventoryStatus}
            selectedSKU={selectedSKU}
            selectedCarrier={selectedCarrier}
          />
        </Location>

        {/* If this is the locations page and the store isn't closed - show the blade open chevron */}
        {isMapList && !isTempClosed && !isKioskMode && <Chevron />}

        {/* If this is reserve a phone or the location blade - allow the user to select a store if its not temporarily closed */}
        {!isMapList && (!isTempClosed || (isReserveList || isKioskWelcome)) && (
          <SelectBtn
            buttonColor="outline"
            buttonSize="small"
            className={isActive ? 'active' : ''}
            aria-label={`${storeLogo?.logo?.altText?.replace('logo', '')} - ${
              store.commonInfo.address.line1
            }, ${store.commonInfo.address.city}, ${
              store.commonInfo.address.province
            } ${store.commonInfo.address.postalCode} - ${
              isActive
                ? magicStrings.selected(locale)
                : magicStrings.selectThisStore(locale)
            }`}
            onClick={() => {
              onClick({
                ...store,
                commonInfo: {
                  ...store?.commonInfo,
                  businessHours: tmsDept?.commonInfo?.businessHours,
                  address: tmsDept?.commonInfo.address,
                  contactInfo: tmsDept?.commonInfo.contactInfo,
                },
              });
            }}
            data-testid="select-button"
            isKioskWelcome={isKioskWelcome}
          >
            {isActive
              ? magicStrings.selected(locale)
              : magicStrings.selectThisStore(locale)}
          </SelectBtn>
        )}

        {/* If this isn't reserve a phone and the store is temporarily closed - we want to direct users to the contact us page */}
        {(!isReserveList && !isKioskWelcome) && isTempClosed && (
          <VirtualMobilistArrow>
            {locale === 'en'
              ? 'Connect with a Virtual Mobilist'
              : 'Communiquez avec un Mobiliste virtuel'}
          </VirtualMobilistArrow>
        )}
      </StoreContact>
    </Store>
  );

  return (
    <>
      {/* If the store is open - just show the store item */}
      {!isMapList && !isTempClosed && storeItem}

      {/* If the store is open and we're in the locations page - open the locations blade for selection */}
      {isMapList && !isTempClosed && (
        <BladeButtonWrapper
          onClick={onClick ? () => onClick(store) : undefined}
          style={{ width: '100%' }}
          isKiosk={isKioskMode}
        >
          {storeItem}
        </BladeButtonWrapper>
      )}

      {/* If the store is closed and we're in the locations page or blade (not reserve a phone) - link to contact us page */}
      {(isMapList || !isReserveList) && isTempClosed && (
        <ContactUsLink href={!isKioskWelcome && `/${locale}/contact-us/?form=assist`}>
          {storeItem}
        </ContactUsLink>
      )}
    </>
  );
};

export default StoreItem;
